
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Database } from '@/integrations/supabase/types';

export interface Persona {
  id: string;
  name: string;
  default_path: string;
}

export const usePersona = () => {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [currentPersona, setCurrentPersona] = useState<Persona | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load persisted persona from localStorage
  useEffect(() => {
    const savedPersona = localStorage.getItem('currentPersona');
    if (savedPersona) {
      try {
        const parsedPersona = JSON.parse(savedPersona);
        setCurrentPersona(parsedPersona);
      } catch (error) {
        console.error('Error parsing saved persona:', error);
      }
    }
  }, []);

  useEffect(() => {
    const fetchPersonas = async () => {
      try {
        setIsLoading(true);
        // Using explicit type casting since the types may not be fully in sync
        const { data, error } = await supabase
          .from('personas')
          .select('id, name, default_path')
          .order('name');
        
        if (error) {
          console.error('Error fetching personas:', error);
          return;
        }
        
        // Cast data to Persona[] type
        const typedData = data as unknown as Persona[];
        setPersonas(typedData);
        
        // Only set a default persona if nothing is persisted
        const persisted = localStorage.getItem('currentPersona');
        if (!persisted) {
          const defaultPersona = typedData.find(p => p.name === 'Development') || typedData[0];
          if (defaultPersona) {
            setCurrentPersona(defaultPersona);
            localStorage.setItem('currentPersona', JSON.stringify(defaultPersona));
          }
        }
      } catch (error) {
        console.error('Error in usePersona hook:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPersonas();
  }, []);

  const switchPersona = (persona: Persona) => {
    setCurrentPersona(persona);
    localStorage.setItem('currentPersona', JSON.stringify(persona));
  };

  return {
    personas,
    currentPersona,
    switchPersona,
    isLoading
  };
};
